import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getNetworkStatus } from "@/lib/api";
import { TreasuryDashboard } from "@/components/status/TreasuryDashboard";
import { LiveRpcCard } from "@/components/status/LiveRpcCard";
import { IncidentHistory } from "@/components/status/IncidentHistory";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Server, Timer, Droplets } from "lucide-react";
import { motion } from "framer-motion";
import { FADE_UP } from "@/lib/motion";

const OVERALL = {
  operational: { label: "All systems operational", dot: "bg-green-500", text: "text-green-400" },
  degraded: { label: "Degraded performance", dot: "bg-yellow-500", text: "text-yellow-400" },
  down: { label: "Node unreachable", dot: "bg-red-500", text: "text-red-400" },
};
const fmt = (n, d = 2) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));

const Metric = ({ label, value, testId }) => (
  <div>
    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{label}</p>
    <p data-testid={testId} className="font-display text-lg font-bold text-white break-all">{value}</p>
  </div>
);

const Section = ({ icon: Icon, title, children, testId }) => (
  <Card data-testid={testId} className="border-[#2A2411] bg-[#0A0A0A]">
    <CardHeader className="pb-3">
      <CardTitle className="text-base font-display flex items-center gap-2"><Icon size={16} className="text-[#D4AF37]" /> {title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const BlockBars = ({ recent }) => {
  const bars = [...recent].reverse().filter((b) => b.interval != null);
  const max = Math.max(...bars.map((b) => b.interval || 0), 1);
  return (
    <div data-testid="status-block-bars" className="flex items-end gap-1 h-20 mt-4">
      {bars.map((b) => (
        <div key={b.number} title={`#${b.number}: ${b.interval ?? "?"}s, ${b.txCount} txs`}
          className="flex-1 rounded-t bg-[#D4AF37]/70 hover:bg-[#D4AF37] transition-colors"
          style={{ height: `${Math.max(((b.interval || 0) / max) * 100, 6)}%` }} />
      ))}
    </div>
  );
};

export default function StatusPage() {
  const { data, isError } = useQuery({ queryKey: ["networkStatus"], queryFn: getNetworkStatus, refetchInterval: 5000 });
  const state = isError ? OVERALL.down : OVERALL[data?.overall] || null;
  const { node, blocks, faucet } = data || {};

  return (
    <div data-testid="status-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <motion.div {...FADE_UP}>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">
          Network <span className="text-[#D4AF37]">Status</span>
        </h1>
        {state && (
          <div data-testid="status-overall" className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className={`relative flex h-3 w-3`}>
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${state.dot}`} />
              <span className={`relative inline-flex rounded-full h-3 w-3 ${state.dot}`} />
            </span>
            <span className={`font-medium ${state.text}`}>{state.label}</span>
            {data && <span className="text-xs text-zinc-600">Updated {new Date(data.checkedAt).toLocaleTimeString()} · refreshes every 5s</span>}
          </div>
        )}
      </motion.div>

      <LiveRpcCard online={!isError && Boolean(node?.online)} />

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section icon={Server} title="Node Health" testId="status-node">
            <div className="grid grid-cols-2 gap-4">
              <Metric label="Status" value={node.online ? "Online" : "Offline"} testId="status-node-online" />
              <Metric label="RPC Latency" value={node.latencyMs != null ? `${node.latencyMs} ms` : "—"} testId="status-node-latency" />
              <Metric label="Chain ID" value={`${node.chainId ?? "—"}${node.chainIdMatches ? "" : " (mismatch)"}`} testId="status-node-chainid" />
              <Metric label="Client" value={node.clientVersion || "—"} />
            </div>
            {node.mode === "local-dev-node" && <Badge className="mt-3 bg-green-500/10 text-green-400 border border-green-500/30">Live network</Badge>}
          </Section>

          <Section icon={Timer} title="Block Production" testId="status-blocks">
            {blocks ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Metric label="Latest Block" value={<Link to={`/block/${blocks.latestBlock}`} className="table-link">{fmt(blocks.latestBlock, 0)}</Link>} testId="status-latest-block" />
                  <Metric label="Last Block" value={`${blocks.lastBlockAgeSec}s ago`} testId="status-last-block-age" />
                  <Metric label="Avg Block Time" value={`${fmt(blocks.avgBlockTime)}s`} testId="status-avg-block-time" />
                  <Metric label="Throughput" value={`${fmt(blocks.tps, 3)} TPS`} />
                </div>
                <BlockBars recent={blocks.recent} />
                <p className="text-[11px] text-zinc-600 mt-1">Block intervals, last {blocks.recent.length} blocks</p>
              </>
            ) : <p className="text-sm text-zinc-500">No block data available.</p>}
          </Section>

          <Section icon={Droplets} title="Faucet Reserves" testId="status-faucet">
            <div className="space-y-3">
              {Object.entries(faucet.tokens).map(([token, t]) => (
                <div key={token} className="flex items-center justify-between p-3 rounded-md bg-[#050505] border border-[#2A2411]">
                  <div>
                    <p data-testid={`status-faucet-${token.toLowerCase()}`} className="font-display font-bold text-white">{fmt(t.balance)} {token}</p>
                    <p className="text-[11px] text-zinc-500">{t.amountPerRequest} {token} per request · alert below {fmt(t.lowThreshold, 0)}</p>
                  </div>
                  <Badge className={t.isLow ? "bg-red-500/10 text-red-400 border border-red-500/30" : "bg-green-500/10 text-green-400 border border-green-500/30"}>
                    {t.isEmpty ? "Empty" : t.isLow ? "Low" : "Healthy"}
                  </Badge>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={Activity} title="Services" testId="status-services">
            <ul className="space-y-2 text-sm">
              {[
                ["Public RPC", node.online],
                ["Block Explorer", node.online],
                ["FMI Faucet", !faucet.tokens.FMI.isDemo && !faucet.tokens.FMI.isEmpty],
                ["USDT Faucet", !faucet.tokens.USDT.isDemo && !faucet.tokens.USDT.isEmpty],
                ["Low-balance Alerts", faucet.alertsEnabled],
              ].map(([name, ok]) => (
                <li key={name} className="flex items-center justify-between">
                  <span className="text-zinc-300">{name}</span>
                  <span className={`text-xs font-semibold ${ok ? "text-green-400" : "text-zinc-500"}`}>{ok ? "Operational" : "Not active"}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      )}
      {data && <IncidentHistory />}
      {data && <TreasuryDashboard />}
    </div>
  );
}
